import { useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  Activity,
  ArrowUpRight,
  Clock3,
  ExternalLink,
  MapPin,
  MonitorSmartphone,
  MousePointerClick,
  Users,
  Wifi,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { MetricCard } from '@/components/financials/MetricCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { cn } from '@/lib/utils';
import { BRAND, CHART_SERIES } from '@/lib/brand';
import { useWebAnalytics } from '@/features/intelligence/hooks';

// The "connected" state is now data-driven: if `web_sessions_daily` has
// rows for the selected range, the dashboard renders live GA4 data; if
// not (no rows / failed sync / never connected), it falls back to a
// clearly-labelled "Sample data" preview so the page still shows operators
// what the live version will look like. See ROADMAP §2.1 + migration 54.

type RangeKey = '7d' | '28d' | '90d';

const RANGE_OPTIONS: Array<{ id: RangeKey; label: string; days: number }> = [
  { id: '7d', label: 'Last 7 days', days: 7 },
  { id: '28d', label: 'Last 28 days', days: 28 },
  { id: '90d', label: 'Last 90 days', days: 90 },
];

export function WebAnalyticsPage() {
  const [range, setRange] = useState<RangeKey>('28d');
  const days = RANGE_OPTIONS.find((r) => r.id === range)?.days ?? 28;

  const liveQuery = useWebAnalytics(range);
  const isConnected = !!liveQuery.data?.connected;

  // Trim the live data to the selected range. The hook fetches the full
  // 90-day window so flipping ranges doesn't re-hit the network.
  const live = useMemo(() => {
    if (!liveQuery.data) return null;
    const trimmed = liveQuery.data.daily.slice(-days);
    return { ...liveQuery.data, daily: trimmed };
  }, [liveQuery.data, days]);

  const sampleDaily = useMemo(() => generateDailySeries(days), [days]);
  const daily = isConnected && live ? live.daily : sampleDaily;

  const sampleTotals = useMemo(() => computeSampleTotals(sampleDaily), [sampleDaily]);
  const totals = isConnected && live
    ? recomputeTotalsForWindow(live.daily)
    : sampleTotals;

  // Panel rows. When connected, we pull the hook's ranked rows; when not,
  // we synthesise rows from the SAMPLE_* shares × the (sample) totals.
  const sourceRows: RankedRow[] = isConnected && live
    ? live.sources.slice(0, 6).map((r) => ({
        key: r.key,
        label: r.label,
        value: r.users,
        pct: r.share,
        hint: SOURCE_HINTS[r.key],
      }))
    : SAMPLE_SOURCES.map((s) => ({
        key: s.name,
        label: s.name,
        value: scale(s.share, totals.users),
        pct: s.share,
        hint: s.hint,
      }));

  const pageRows: RankedRow[] = isConnected && live
    ? live.pages.slice(0, 6).map((r) => ({
        key: r.key,
        label: r.key,
        value: r.views ?? r.sessions,
        pct: r.share,
        hint: r.hint,
      }))
    : SAMPLE_PAGES.map((p) => ({
        key: p.path,
        label: p.path,
        value: scale(p.share, totals.sessions),
        pct: p.share,
        hint: p.title,
      }));

  const deviceRows: RankedRow[] = isConnected && live
    ? live.devices.slice(0, 6).map((r) => ({
        key: r.key,
        label: r.label,
        value: r.sessions,
        pct: r.share,
      }))
    : SAMPLE_DEVICES.map((d) => ({
        key: d.name,
        label: d.name,
        value: scale(d.share, totals.sessions),
        pct: d.share,
      }));

  const geoRows: RankedRow[] = isConnected && live
    ? live.geo.slice(0, 8).map((r) => ({
        key: r.key,
        label: r.label,
        value: r.users,
        pct: r.share,
      }))
    : SAMPLE_GEO.map((g) => ({
        key: g.country,
        label: g.country,
        value: scale(g.share, totals.users),
        pct: g.share,
      }));

  const eventRows: RankedRow[] = isConnected && live
    ? // Prefer key events first; if the property hasn't marked any yet,
      // fall back to the top events overall so the panel never goes blank.
      (() => {
        const keyOnly = live.events.filter((e) => e.hint === 'Key event');
        const source = keyOnly.length > 0 ? keyOnly : live.events;
        return source.slice(0, 6).map((r) => ({
          key: r.key,
          label: r.label,
          value: r.users,
          pct: r.share,
          hint: EVENT_HINTS[r.key] ?? r.hint,
        }));
      })()
    : SAMPLE_EVENTS.map((e) => ({
        key: e.name,
        label: e.name,
        value: scale(e.share, totals.conversions),
        pct: e.share,
        hint: e.hint,
      }));

  return (
    <div className="space-y-6">
      <PageHeader
        title="Web analytics"
        actions={
          <div className="flex items-center gap-2">
            {isConnected ? (
              <Badge variant="success">GA4 connected</Badge>
            ) : (
              <Badge variant="warning">GA4 not connected</Badge>
            )}
            <Button variant="outline" asChild>
              <a
                href="https://analytics.google.com"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center"
              >
                Open GA4 <ExternalLink className="h-4 w-4" />
              </a>
            </Button>
          </div>
        }
      />

      {!isConnected && (
        <Card className="border-atmosphere-dark/30 bg-atmosphere/10">
          <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <Wifi className="mt-0.5 h-5 w-5 shrink-0 text-atmosphere-dark" strokeWidth={1.8} />
              <div>
                <p className="text-sm font-medium text-foreground">
                  Showing sample data
                </p>
                <p className="text-xs text-muted-foreground">
                  {liveQuery.isLoading
                    ? 'Loading live GA4 data — sample numbers shown while we fetch.'
                    : liveQuery.error
                      ? 'Couldn\'t load GA4 data. The numbers below are illustrative; check that GA4 is connected on /integrations.'
                      : <>The numbers below are illustrative until a Super Admin connects GA4 on <code className="rounded bg-secondary px-1">/integrations</code>. Once connected, the hourly <code className="rounded bg-secondary px-1">web-analytics-sync</code> Edge Function will populate this dashboard.</>}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Date-range selector ─ Tabs is the only built-in segmented
          control in the design system; using it without TabsContent
          keeps the page state in a single React useState instead of
          forking radix focus management. */}
      <Tabs value={range} onValueChange={(v) => setRange(v as RangeKey)}>
        <TabsList>
          {RANGE_OPTIONS.map((r) => (
            <TabsTrigger key={r.id} value={r.id}>
              {r.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {/* Hidden content is required for radix to mount triggers cleanly. */}
        {RANGE_OPTIONS.map((r) => (
          <TabsContent key={r.id} value={r.id} className="hidden" />
        ))}
      </Tabs>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          label="Users"
          value={totals.users.toLocaleString()}
          icon={<Users className="h-4 w-4 text-muted-foreground" />}
          hint={`${totals.newUsers.toLocaleString()} new (${pct(totals.newUsers, totals.users)})`}
        />
        <MetricCard
          label="Sessions"
          value={totals.sessions.toLocaleString()}
          icon={<Activity className="h-4 w-4 text-muted-foreground" />}
          hint={`${avgPerDay(totals.sessions, days)}/day average`}
        />
        <MetricCard
          label="Engagement rate"
          value={`${(totals.engagementRate * 100).toFixed(1)}%`}
          icon={<MousePointerClick className="h-4 w-4 text-muted-foreground" />}
          hint={`${formatDuration(totals.avgEngagementSeconds)} avg / session`}
        />
        <MetricCard
          label="Conversions"
          value={totals.conversions.toLocaleString()}
          icon={<ArrowUpRight className="h-4 w-4 text-bright-moss-dark" />}
          hint={`${(totals.conversions / Math.max(1, totals.sessions) * 100).toFixed(2)}% conversion`}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Users per day</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={daily}>
                <defs>
                  <linearGradient id="users-fill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CHART_SERIES[0]} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={CHART_SERIES[0]} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={BRAND.linenDark} vertical={false} />
                <XAxis
                  dataKey="day"
                  stroke={BRAND.oldGrowth}
                  fontSize={11}
                  tickFormatter={shortDay}
                />
                <YAxis stroke={BRAND.oldGrowth} fontSize={11} />
                <Tooltip
                  formatter={(v: number, k: string) =>
                    k === 'users' ? [v.toLocaleString(), 'Users'] : [v.toLocaleString(), 'New users']
                  }
                  labelFormatter={(label) => formatDay(label as string)}
                  contentStyle={{
                    background: '#fff',
                    border: `1px solid ${BRAND.linenDark}`,
                    borderRadius: 8,
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="users"
                  stroke={CHART_SERIES[0]}
                  strokeWidth={2}
                  fill="url(#users-fill)"
                />
                <Area
                  type="monotone"
                  dataKey="newUsers"
                  stroke={CHART_SERIES[3]}
                  strokeWidth={1.5}
                  fill="transparent"
                  strokeDasharray="4 3"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 flex flex-wrap gap-4 text-xs text-muted-foreground">
            <LegendDot color={CHART_SERIES[0]} label="All users" />
            <LegendDot color={CHART_SERIES[3]} label="New users" dashed />
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Traffic sources</CardTitle>
          </CardHeader>
          <CardContent>
            <RankedList rows={sourceRows} formatValue={(v) => v.toLocaleString()} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Top pages</CardTitle>
          </CardHeader>
          <CardContent>
            <RankedList rows={pageRows} formatValue={(v) => v.toLocaleString()} />
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MonitorSmartphone className="h-4 w-4 text-muted-foreground" />
              Devices
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={deviceRows.map((d) => ({ name: d.label, sessions: d.value }))}
                  layout="vertical"
                  margin={{ left: 4, right: 8 }}
                >
                  <CartesianGrid stroke={BRAND.linenDark} horizontal={false} />
                  <XAxis type="number" stroke={BRAND.oldGrowth} fontSize={11} />
                  <YAxis
                    dataKey="name"
                    type="category"
                    stroke={BRAND.oldGrowth}
                    fontSize={11}
                    width={70}
                  />
                  <Tooltip
                    formatter={(v: number) => v.toLocaleString()}
                    contentStyle={{
                      background: '#fff',
                      border: `1px solid ${BRAND.linenDark}`,
                      borderRadius: 8,
                    }}
                  />
                  <Bar dataKey="sessions" fill={CHART_SERIES[1]} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              Top countries
            </CardTitle>
          </CardHeader>
          <CardContent>
            <RankedList rows={geoRows} formatValue={(v) => v.toLocaleString()} compact />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock3 className="h-4 w-4 text-muted-foreground" />
              Conversion events
            </CardTitle>
          </CardHeader>
          <CardContent>
            <RankedList rows={eventRows} formatValue={(v) => v.toLocaleString()} compact />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ─── Small presentational helpers ─────────────────────────────────────

function LegendDot({
  color,
  label,
  dashed,
}: {
  color: string;
  label: string;
  dashed?: boolean;
}) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        aria-hidden
        className={cn(
          'inline-block h-0.5 w-4 rounded',
          dashed && 'border-t border-dashed bg-transparent',
        )}
        style={dashed ? { borderColor: color } : { backgroundColor: color }}
      />
      {label}
    </span>
  );
}

interface RankedRow {
  key: string;
  label: string;
  value: number;
  pct: number;
  hint?: string;
}

function RankedList({
  rows,
  formatValue,
  compact,
}: {
  rows: RankedRow[];
  formatValue: (v: number) => string;
  compact?: boolean;
}) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <ul className="space-y-2.5">
      {rows.map((r) => (
        <li key={r.key} className="space-y-1">
          <div className="flex items-baseline justify-between gap-3">
            <div className={cn('min-w-0', compact ? '' : 'flex-1')}>
              <p className="truncate text-sm font-medium text-foreground">
                {r.label}
              </p>
              {r.hint && !compact && (
                <p className="truncate text-xs text-muted-foreground">{r.hint}</p>
              )}
            </div>
            <div className="shrink-0 text-right">
              <p className="tabular-nums text-sm text-foreground">
                {formatValue(r.value)}
              </p>
              <p className="text-[11px] text-muted-foreground">
                {(r.pct * 100).toFixed(1)}%
              </p>
            </div>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-secondary">
            <div
              className="h-full rounded-full bg-old-growth/70"
              style={{ width: `${Math.max(2, (r.value / max) * 100)}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

// ─── Mock data ─────────────────────────────────────────────────────────
//
// Used when GA4 isn't connected yet, or while the hook is still loading.
// Lives alongside the real-data renderer at the top of the component so
// every panel has a sane fallback.

const SAMPLE_SOURCES = [
  { name: 'Organic Search', share: 0.34, hint: 'Google, Bing — non-paid' },
  { name: 'Direct', share: 0.22, hint: 'Typed URL or bookmarked' },
  { name: 'Organic Social', share: 0.18, hint: 'Instagram, Facebook, TikTok' },
  { name: 'Paid Search', share: 0.11, hint: 'Google Ads, Bing Ads' },
  { name: 'Email', share: 0.08, hint: 'Klaviyo, Shopify Email' },
  { name: 'Referral', share: 0.07, hint: 'Inbound links from other sites' },
];

// Hints displayed alongside each GA4 channel grouping. GA4's
// `sessionDefaultChannelGrouping` values are fixed strings, so the lookup
// is exhaustive enough; unmatched channels just render without a hint.
const SOURCE_HINTS: Record<string, string> = {
  'Organic Search': 'Google, Bing — non-paid',
  Direct: 'Typed URL or bookmarked',
  'Organic Social': 'Instagram, Facebook, TikTok',
  'Paid Search': 'Google Ads, Bing Ads',
  'Paid Social': 'Meta Ads, TikTok Ads',
  Email: 'Klaviyo, Shopify Email',
  Referral: 'Inbound links from other sites',
  Display: 'Google Display, programmatic',
  Affiliates: 'Affiliate networks',
  Unassigned: 'GA4 could not classify',
};

// Friendly descriptions for the most common GA4 ecommerce events. Anything
// not in this map falls back to the hint the hook supplies (e.g. "Key event").
const EVENT_HINTS: Record<string, string> = {
  purchase: 'Order placed',
  begin_checkout: 'Checkout started',
  add_to_cart: 'Item added',
  view_item: 'Product page viewed',
  add_payment_info: 'Payment info entered',
  add_shipping_info: 'Shipping info entered',
  sign_up: 'Account created',
  generate_lead: 'Lead captured',
};

function computeSampleTotals(daily: DailyPoint[]) {
  const users = daily.reduce((s, d) => s + d.users, 0);
  const newUsers = daily.reduce((s, d) => s + d.newUsers, 0);
  const sessions = daily.reduce((s, d) => s + d.sessions, 0);
  const conversions = daily.reduce((s, d) => s + d.conversions, 0);
  const engaged = daily.reduce((s, d) => s + d.engagedSessions, 0);
  const engagementRate = sessions === 0 ? 0 : engaged / sessions;
  const avgEngagementSeconds = daily.length
    ? daily.reduce((s, d) => s + d.avgEngagementSeconds, 0) / daily.length
    : 0;
  return {
    users,
    newUsers,
    sessions,
    engagedSessions: engaged,
    conversions,
    engagementRate,
    avgEngagementSeconds,
  };
}

// Live-data variant of computeSampleTotals: same arithmetic but reads
// from the hook's daily rows (which already carry per-day averages, so
// we re-derive total seconds via sessions × avg to compute the window
// engagement rate / average accurately when the user trims the range).
function recomputeTotalsForWindow(
  daily: Array<{
    users: number;
    newUsers: number;
    sessions: number;
    engagedSessions: number;
    conversions: number;
    avgEngagementSeconds: number;
  }>,
) {
  const users = daily.reduce((s, d) => s + d.users, 0);
  const newUsers = daily.reduce((s, d) => s + d.newUsers, 0);
  const sessions = daily.reduce((s, d) => s + d.sessions, 0);
  const engaged = daily.reduce((s, d) => s + d.engagedSessions, 0);
  const conversions = daily.reduce((s, d) => s + d.conversions, 0);
  const totalEngagementSeconds = daily.reduce(
    (s, d) => s + d.avgEngagementSeconds * d.sessions,
    0,
  );
  return {
    users,
    newUsers,
    sessions,
    engagedSessions: engaged,
    conversions,
    engagementRate: sessions === 0 ? 0 : engaged / sessions,
    avgEngagementSeconds: sessions === 0 ? 0 : totalEngagementSeconds / sessions,
  };
}

const SAMPLE_PAGES = [
  { path: '/', title: 'Home', share: 0.31 },
  { path: '/products/skincare-essentials', title: 'Skincare essentials', share: 0.19 },
  { path: '/collections/all', title: 'All products', share: 0.14 },
  { path: '/pages/about', title: 'About Virro', share: 0.09 },
  { path: '/cart', title: 'Shopping cart', share: 0.08 },
  { path: '/products/daily-hydrator', title: 'Daily hydrator', share: 0.07 },
];

const SAMPLE_DEVICES = [
  { name: 'Mobile', share: 0.62 },
  { name: 'Desktop', share: 0.31 },
  { name: 'Tablet', share: 0.07 },
];

const SAMPLE_GEO = [
  { country: 'United States', share: 0.78 },
  { country: 'Canada', share: 0.08 },
  { country: 'United Kingdom', share: 0.05 },
  { country: 'Australia', share: 0.03 },
  { country: 'Germany', share: 0.02 },
];

const SAMPLE_EVENTS = [
  { name: 'purchase', share: 0.18, hint: 'Order placed' },
  { name: 'begin_checkout', share: 0.42, hint: 'Checkout started' },
  { name: 'add_to_cart', share: 0.40, hint: 'Item added' },
];

// ─── Series generator ─────────────────────────────────────────────────
//
// Deterministic mulberry32 seeded by day index so the chart looks
// realistic (weekly seasonality + light noise) but stays stable
// across re-renders. Will be replaced by real GA4 series once §2.1
// lands.

interface DailyPoint {
  day: string;
  users: number;
  newUsers: number;
  sessions: number;
  engagedSessions: number;
  conversions: number;
  avgEngagementSeconds: number;
}

function generateDailySeries(days: number): DailyPoint[] {
  const out: DailyPoint[] = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const dow = d.getDay();
    const rnd = mulberry32(d.getTime() / 86_400_000);
    const seasonality = dow === 0 || dow === 6 ? 0.78 : 1.05;
    const trend = 1 + (days - i) / (days * 8);
    const noise = 0.85 + rnd() * 0.3;
    const users = Math.round(240 * seasonality * trend * noise);
    const newUsers = Math.round(users * (0.55 + rnd() * 0.1));
    const sessions = Math.round(users * (1.25 + rnd() * 0.15));
    const engagedSessions = Math.round(sessions * (0.55 + rnd() * 0.1));
    const conversions = Math.round(sessions * (0.012 + rnd() * 0.006));
    const avgEngagementSeconds = Math.round(85 + rnd() * 40);
    out.push({
      day: d.toISOString().slice(0, 10),
      users,
      newUsers,
      sessions,
      engagedSessions,
      conversions,
      avgEngagementSeconds,
    });
  }
  return out;
}

function mulberry32(seed: number) {
  let a = Math.floor(seed) || 1;
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function scale(share: number, total: number): number {
  return Math.max(0, Math.round(share * total));
}

function pct(part: number, whole: number): string {
  if (whole === 0) return '0%';
  return `${((part / whole) * 100).toFixed(1)}%`;
}

function avgPerDay(total: number, days: number): string {
  if (days === 0) return '0';
  return Math.round(total / days).toLocaleString();
}

function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '0s';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function shortDay(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function formatDay(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}
